import { reactive } from "vue";
import { getDatabase, ref as dbRef, onValue } from "firebase/database";
import { authStore } from "@/store/auth.js";
import { cloudFunctions } from "@/store/cloudFunctions.js";
import { pricingStore } from "@/store/pricing.js";
import { useToast } from "@/composables/useToast.js";
import { showErrorToast } from "@/scripts/friendlyError.js";
import i18n from "@/locales";

let store = null;
let CdvPurchase = null;
let initialized = false;
// Platform is static for the app's lifetime; capture it at module load so
// getProductPrice and other helpers are correct even if called before
// initialize() completes. On web (no Capacitor) this is false, which is
// fine — initialize() no-ops on web anyway.
let isAndroidPlatform =
  typeof window !== "undefined" &&
  window.Capacitor?.getPlatform?.() === "android";
// Reactive on purpose. The store delivers products asynchronously, and on a
// native cold start productUpdated fires AFTER the plan surfaces have already
// rendered. A plain object gives them no dependency to re-render on, so the
// USD catalog fallback in getProductPrice would stick permanently and a store
// with a localized currency would never be shown. Reads inside a computed now
// track the cache and rerender when it warms.
const productPrices = reactive({});

// cordova-plugin-purchase v13 GooglePlay replacement (proration) mode. The
// plugin's ProrationMode enum values are STRINGS (it forwards the name to the
// Android billing client), not the integer constants from the BillingClient
// docs, so we use the string literal directly. Reading it off window.CdvPurchase
// at import time does not work: this module is imported before initialize()
// dynamically imports the plugin, so the enum is not yet defined and we would
// capture the fallback.
//
// Foundation and Fluency are auto-renewing base plans inside ONE subscription
// product (articulearn_premium), so every in-app upgrade is a same-subscription
// auto-renew switch. Google only permits CHARGE_FULL_PRICE or WITHOUT_PRORATION
// for that transition; CHARGE_PRORATED_PRICE is rejected up front with
// DEVELOPER_ERROR (verified on device). CHARGE_FULL_PRICE applies the upgrade
// immediately, charges the full new price now, and starts a fresh billing cycle.
// https://developer.android.com/google/play/billing/subscriptions#change
const REPLACEMENT_MODE_IMMEDIATE_AND_CHARGE_FULL_PRICE =
  "IMMEDIATE_AND_CHARGE_FULL_PRICE";

/**
 * Find the store offer matching a Google Play base plan id. cordova-plugin-purchase
 * v13 builds offer ids as "<productId>@<basePlanId>" (or "<productId>@<basePlanId>@<offerId>"
 * when a promo offer is attached), NOT the bare base plan id. Match against the
 * product-prefixed id so we never silently fall through to the wrong offer.
 */
export function findOfferForBasePlan(product, basePlanId) {
  const offers = product?.offers;
  if (!offers || offers.length === 0) return null;
  const fullId = `${product.id}@${basePlanId}`;
  const match = offers.find(
    (o) =>
      o.id === fullId ||
      (typeof o.id === "string" && o.id.startsWith(`${fullId}@`))
  );
  // No silent fallback to offers[0]: charging the wrong base plan is worse than
  // surfacing a missing-offer error to the caller.
  return match || null;
}

/**
 * Build the additionalData for store.order. For an upgrade (the subscription is
 * already owned) Google needs the old purchase token + a replacement mode so it
 * switches the base plan in place instead of creating a second subscription.
 */
export function buildGooglePlayOrderData({ oldPurchaseToken }) {
  if (!oldPurchaseToken) return undefined;
  return {
    googlePlay: {
      oldPurchaseToken,
      prorationMode: REPLACEMENT_MODE_IMMEDIATE_AND_CHARGE_FULL_PRICE,
    },
  };
}

function getOwnedGooglePurchaseToken() {
  return iapUtils.getOwnedPurchaseTokenInternal();
}

// After a purchase is approved, the entitlement is written asynchronously by the
// backend (validateIapReceipt). Wait for users/{uid}/billing to reflect the
// purchased plan before resolving the purchase, so the caller refreshes against
// fresh data instead of the stale plan. Times out so a failed or slow validation
// never hangs the purchase call.
const ENTITLEMENT_TIMEOUT_MS = 45000;
const ACTIVE_BILLING_STATUSES = new Set(["active", "trialing"]);

function waitForEntitlement(expectedLookupKey) {
  const uid = authStore.getUserId();
  if (!uid) return Promise.resolve(false);
  return new Promise((resolve) => {
    let unsubscribe = null;
    let timer = null;
    let settled = false;
    const cleanup = () => {
      if (unsubscribe) unsubscribe();
      if (timer) clearTimeout(timer);
    };
    const finish = (ok) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(ok);
    };
    const billingRef = dbRef(getDatabase(), `users/${uid}/billing`);
    unsubscribe = onValue(
      billingRef,
      (snapshot) => {
        const billing = snapshot.val() || {};
        const statusOk = ACTIVE_BILLING_STATUSES.has(billing.status);
        // Match the purchased plan, not just any active status, so an upgrade
        // from an already-active plan isn't confirmed against the old plan.
        const planOk =
          !expectedLookupKey || billing.lookupKey === expectedLookupKey;
        if (statusOk && planOk) finish(true);
      },
      () => finish(false)
    );
    // Guard against onValue firing synchronously (e.g. cached data): if it
    // already settled, unsubscribe now (cleanup ran above while unsubscribe was
    // still null); otherwise arm the timeout.
    if (settled) {
      cleanup();
    } else {
      timer = setTimeout(() => finish(false), ENTITLEMENT_TIMEOUT_MS);
    }
  });
}

// The store price cache lookup, for BOTH platforms. Held in one place and used
// by both iapUtils.getCachedStorePrice and iapUtils.getProductPrice, so the only
// thing that differs between them is getProductPrice's USD catalog fallback.
// That is what makes "the price the store reported" and "the price we would
// display" distinguishable without the two drifting apart.
//
// Not split into a pure (basePlanId) => key resolver, deliberately: the Android
// branch resolves its key by scanning LIVE CACHE CONTENTS for a promo offer, so
// a key function cannot express it, and any user on a promo offer would lose
// their cached price.
//
// ONE deliberate behavior difference from the pre-refactor getProductPrice, and
// only on Android. Before, a promo key holding a FALSY value was returned as-is
// and the catalog fallback was never reached; now the falsy value reads as "no
// cached price" and getProductPrice falls back to the catalog. That state cannot
// occur in production — the sole writer of the cache is the productUpdated
// callback, which guards `if (price)` — but _setProductPricesForTest can create
// it, so it is recorded rather than left to be rediscovered.
function readCachedStorePrice(basePlanId) {
  if (!basePlanId || typeof basePlanId !== "string") return "";
  if (!isAndroidPlatform) {
    // iOS: prices are cached by the legacy standalone product id.
    const legacyId = basePlanId.replace("-", "_");
    if (productPrices[legacyId]) return productPrices[legacyId];
    return "";
  }
  // Android: productPrices is keyed by the plugin's offer id
  // ("<productId>@<basePlanId>", optionally with a trailing "@<offerId>" for
  // promo offers), so build the same prefixed key here rather than looking up
  // the bare base plan id.
  //
  // The exact-key check tests TRUTHINESS, not presence, on purpose: a falsy
  // value must fall through to the promo scan the same way it always has. A
  // presence check (`in`) would return the falsy value and never scan.
  const offerId = `${pricingStore.iapProductId}@${basePlanId}`;
  if (productPrices[offerId]) {
    return productPrices[offerId];
  }
  const prefixed = Object.keys(productPrices).find((k) =>
    k.startsWith(`${offerId}@`)
  );
  // `|| ""` honours the string-or-empty contract: the scan finds a key that
  // exists, whose value could in principle be falsy (see the note above).
  if (prefixed) return productPrices[prefixed] || "";
  return "";
}

export const iapUtils = {
  /**
   * Initialize the IAP store. Must be called once on app startup (only on native).
   * Safe to call on web — will no-op.
   */
  async initialize() {
    if (initialized) return;
    if (
      typeof window === "undefined" ||
      window.Capacitor?.isNativePlatform?.() !== true
    ) {
      return;
    }

    try {
      // Dynamic import — sets window.CdvPurchase global (not a named export)
      await import("cordova-plugin-purchase");
      CdvPurchase = window.CdvPurchase;
      store = CdvPurchase.store;

      if (!store) {
        console.error("CdvPurchase.store not available");
        return;
      }

      // Determine platform
      const platform = window.Capacitor.getPlatform?.();
      isAndroidPlatform = platform === "android";
      const storePlatform =
        platform === "ios"
          ? CdvPurchase.Platform.APPLE_APPSTORE
          : CdvPurchase.Platform.GOOGLE_PLAY;

      // iOS still uses the four legacy standalone StoreKit product ids; the
      // single-product base-plan model is Google Play only. Registering the
      // wrong ids on a platform breaks product/offer loading and purchases.
      if (storePlatform === CdvPurchase.Platform.APPLE_APPSTORE) {
        const appleProducts = [
          "foundation_monthly",
          "foundation_yearly",
          "fluency_monthly",
          "fluency_yearly",
        ];
        appleProducts.forEach((id) => {
          store.register({
            id,
            type: CdvPurchase.ProductType.PAID_SUBSCRIPTION,
            platform: storePlatform,
          });
        });
      } else {
        // Android: register the single subscription product. Its base plans
        // surface as offers under this one product.
        store.register({
          id: pricingStore.iapProductId,
          type: CdvPurchase.ProductType.PAID_SUBSCRIPTION,
          platform: storePlatform,
        });
      }

      // Validate purchases server-side. cordova-plugin-purchase passes a
      // Validator.Request.Body; map it to the shape validateIapReceipt expects
      // (top-level `platform`, plus transaction.{purchaseToken, orderId,
      // productId}) rather than posting the raw plugin body, whose platform is
      // under transaction.type and whose orderId is transaction.id.
      store.validator = async (receipt, callback) => {
        try {
          const token = await authStore.getIdToken();
          if (!token) {
            callback({ ok: false, message: "Not authenticated" });
            return;
          }
          const transaction = receipt?.transaction || {};
          const body = {
            platform: window.Capacitor?.getPlatform?.(),
            id: receipt?.id,
            transaction: {
              id: transaction.id,
              purchaseToken: transaction.purchaseToken,
              // The plugin exposes Google's orderId as transaction.id.
              orderId: transaction.id,
              productId: receipt?.id,
            },
          };
          const response = await fetch(cloudFunctions.getIapValidateApi(), {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(body),
          });
          const result = await response.json();
          callback(result);
        } catch (error) {
          console.error("IAP validation error:", error);
          callback({ ok: false, message: error.message });
        }
      };

      // Transaction lifecycle: approved -> verify() (invokes the validator
      // above) -> verified -> finish() (acknowledges the purchase so the store
      // does not auto-refund it). Without the .approved() step, purchases were
      // charged but never validated, granted, or acknowledged.
      //
      // unverified: when the backend rejects a transaction as EXPIRED
      // (VALIDATOR_SUBSCRIPTION_EXPIRED), finish exactly that transaction.
      // A rejected transaction is otherwise never finished, and an unfinished
      // transaction wedges the StoreKit queue: StoreKit redelivers it on every
      // order() without showing a payment sheet, permanently blocking
      // purchases on the device. Only the expired verdict may clear anything
      // — every other failure (transient network, backend 5xx, generic
      // rejection) must stay in the queue so validation can retry, because
      // finishing acknowledges a possibly-paid purchase without granting it.
      // And only the transaction the backend named: receipt.finish() would
      // finish EVERY transaction in the iOS application receipt, including a
      // new in-flight purchase that raced in. No echoed id -> no cleanup.
      store
        .when()
        .approved((transaction) => transaction.verify())
        .verified((receipt) => receipt.finish())
        .unverified(({ receipt, payload }) => {
          if (payload?.code !== CdvPurchase.ErrorCode.VALIDATOR_SUBSCRIPTION_EXPIRED) {
            return;
          }
          const expiredId = payload?.data?.transaction?.id;
          if (!expiredId) return;
          (receipt?.transactions || [])
            .filter((t) => t.transactionId === expiredId)
            .forEach((t) => t.finish());
        });

      // Cache prices as products/offers load. Android keys by the offer id
      // (one product, many base-plan offers); iOS keys by the legacy product id
      // (four standalone products, one offer each).
      store.when().productUpdated((product) => {
        if (isAndroidPlatform) {
          if (product.id !== pricingStore.iapProductId) return;
          (product.offers || []).forEach((offer) => {
            const price = offer.pricingPhases?.[0]?.price;
            if (price) productPrices[offer.id] = price;
          });
        } else {
          const offer = product.offers?.[0];
          const price = offer?.pricingPhases?.[0]?.price;
          if (price) productPrices[product.id] = price;
        }
      });

      // Initialize the store
      await store.initialize([storePlatform]);

      initialized = true;
      console.log("IAP store initialized");
    } catch (error) {
      console.error("Failed to initialize IAP store:", error);
      const { showToast } = useToast();
      showToast(i18n.global.t("errors.purchase.initFailed"), "error");
    }
  },

  /**
   * Return the purchase token of the currently-owned subscription, or null.
   * Used to drive an in-place plan switch (upgrade).
   *
   * During the migration window a user may still own a legacy standalone Google
   * product (foundation_monthly ... fluency_yearly) while upgrading into the new
   * articulearn_premium base-plan product. If only the new product id is
   * matched, those legacy owners get no oldPurchaseToken and the upgrade goes
   * out as a fresh purchase, which can create the second concurrent
   * subscription this PR exists to fix. So match the new product id AND the
   * four legacy Google product ids when selecting the owned token.
   *
   * We must not blindly return the first token in localReceipts: a stale or
   * expired transaction can linger there, and passing an inactive token as
   * oldPurchaseToken makes Google reject the replacement. Filter to
   * transactions for our subscription products that are in an active state
   * (APPROVED or FINISHED), then prefer the most recently purchased one.
   * cordova-plugin-purchase v13 exposes the Google token under
   * transaction.nativePurchase.purchaseToken, with purchaseId as a fallback.
   * Field path is verified on device.
   */
  getOwnedPurchaseTokenInternal() {
    if (!store || !CdvPurchase) return null;
    const activeStates = [
      CdvPurchase.TransactionState.APPROVED,
      CdvPurchase.TransactionState.FINISHED,
    ];
    const ownedProductIds = new Set([
      pricingStore.iapProductId,
      "foundation_monthly",
      "foundation_yearly",
      "fluency_monthly",
      "fluency_yearly",
    ]);
    const candidates = [];
    for (const receipt of store.localReceipts || []) {
      for (const txn of receipt.transactions || []) {
        const isForProduct = (txn.products || []).some((p) =>
          ownedProductIds.has(p.id)
        );
        if (!isForProduct || !activeStates.includes(txn.state)) continue;
        // Exclude expired transactions: a stale canceled subscription can
        // linger in localReceipts with an APPROVED/FINISHED state. Passing its
        // token as oldPurchaseToken would make Google reject the replacement.
        if (txn.expirationDate instanceof Date && txn.expirationDate <= new Date())
          continue;
        const token = txn.nativePurchase?.purchaseToken || txn.purchaseId;
        if (token) candidates.push(txn);
      }
    }
    if (candidates.length === 0) return null;
    candidates.sort((a, b) => {
      const getTime = (d) => {
        if (!d) return 0;
        if (d instanceof Date) return d.getTime();
        const parsed = new Date(d).getTime();
        return isNaN(parsed) ? 0 : parsed;
      };
      return getTime(b.purchaseDate) - getTime(a.purchaseDate);
    });
    const best = candidates[0];
    return best.nativePurchase?.purchaseToken || best.purchaseId || null;
  },

  /**
   * Purchase or upgrade to a Google Play base plan.
   * @param {string} basePlanId - e.g. "foundation-monthly"
   * @returns {Promise<boolean>} true once the entitlement write landed; false
   *   if the store order completed but the entitlement did not arrive within
   *   the timeout, and false for a cancelled or failed order. A false return
   *   is not proof the purchase failed, only that it is not confirmed yet.
   */
  async purchase(basePlanId) {
    if (!basePlanId || typeof basePlanId !== "string") {
      console.error("Invalid basePlanId");
      return false;
    }
    if (!store || !CdvPurchase) {
      // Say so. Every other early return in this method already tells the
      // user something, and a caller cannot say it for them: false also means
      // "cancelled" and "not confirmed yet", so a caller that spoke here would
      // be wrong in those cases. Silence turned a tapped purchase button into
      // a dead end with no message at all (errors.purchase.initFailed was
      // authored for this branch and had no call site).
      console.error("IAP store not initialized");
      useToast().showToast(i18n.global.t("errors.purchase.initFailed"), "error");
      return false;
    }

    const { showToast } = useToast();
    try {
      // Android uses the single subscription product; iOS still uses the four
      // legacy standalone StoreKit product ids. The base plan id and the legacy
      // Apple product id differ only by hyphen-vs-underscore.
      const productId = isAndroidPlatform
        ? pricingStore.iapProductId
        : basePlanId.replace("-", "_");
      const product = store.get(productId);
      if (!product) {
        console.error(`Product not found: ${productId}`);
        showToast(i18n.global.t("errors.purchase.unavailable"), "error");
        return false;
      }

      const offer = isAndroidPlatform
        ? findOfferForBasePlan(product, basePlanId)
        : product.getOffer();
      if (!offer) {
        console.error(`No offer found for: ${basePlanId}`);
        showToast(i18n.global.t("errors.purchase.unavailable"), "error");
        return false;
      }

      // If the subscription is already owned (Android only) this is an upgrade:
      // pass the old purchase token + replacement mode so Google switches the
      // base plan in place rather than creating a second subscription. iOS
      // upgrades are handled by App Store auto-renew; no replacement data.
      const oldPurchaseToken = isAndroidPlatform
        ? getOwnedGooglePurchaseToken()
        : null;

      // Guard the Google Play upgrade path: if the user already owns the
      // subscription (any owned base plan / the single subscription product) but
      // we couldn't resolve the old purchase token, ordering anyway would go out
      // as a FRESH purchase and create a second concurrent subscription instead
      // of replacing the current plan. Abort with a clear error instead. Only
      // applies to genuine upgrades (Android, subscription already owned); a
      // first-time purchase (nothing owned) falls through unaffected.
      // Check all product IDs that getOwnedPurchaseTokenInternal searches,
      // including legacy standalone SKUs for users migrating to the new model.
      const hasOwnedActiveSubscription = [
        pricingStore.iapProductId,
        "foundation_monthly",
        "foundation_yearly",
        "fluency_monthly",
        "fluency_yearly",
      ].some((id) => iapUtils.isOwned(id));
      if (isAndroidPlatform && !oldPurchaseToken && hasOwnedActiveSubscription) {
        console.error(
          "Upgrade aborted: owned subscription but no old purchase token resolved"
        );
        showToast(i18n.global.t("errors.purchase.missingSubscription"), "error");
        return false;
      }

      const additionalData = buildGooglePlayOrderData({ oldPurchaseToken });

      const result = await store.order(offer, additionalData);
      if (result && result.isError) {
        if (result.code === CdvPurchase.ErrorCode.PAYMENT_CANCELLED) {
          console.log("Purchase cancelled by user");
          return false;
        }
        console.error("Purchase error:", result.message);
        showErrorToast(result, "purchase");
        return false;
      }

      // Wait for users/{uid}/billing to reflect the purchased plan (status
      // active + matching lookupKey) before the caller refreshes, so an upgrade
      // is not confirmed against the old plan.
      const plan = pricingStore.getPlanByBasePlanId(basePlanId);
      let expectedLookupKey = null;
      if (plan.id !== "free") {
        expectedLookupKey = basePlanId.includes("yearly")
          ? plan.yearlyLookupKey
          : plan.monthlyLookupKey;
      }
      const entitled = await waitForEntitlement(expectedLookupKey);
      if (!entitled) {
        // The store order completed but we never observed users/{uid}/billing
        // reflect the plan. Three different things land here and the client
        // cannot tell them apart: propagation lag, a cancelled billing
        // listener, and a permanent validateIapReceipt rejection (the backend
        // never reports its verdict to the client). In two of the three the
        // plan never unlocks on its own, so the copy forecasts nothing and
        // points at Restore Purchases, which recovers all three.
        // What we do know is that this is NOT a cancel and NOT a store error:
        // both of those returned above.
        showToast(i18n.global.t("errors.purchase.pending"), "info", 10000);
      }

      return entitled;
    } catch (error) {
      console.error("Purchase failed:", error);
      showErrorToast(error, "purchase");
      return false;
    }
  },

  /**
   * Get the price the store has actually reported for a base plan, or "" when it
   * has reported none. NEVER falls back to the USD catalog — that is the whole
   * point of this accessor. A caller cannot use getProductPrice to answer
   * "does the store have a price for this yet?", because getProductPrice returns
   * the catalog literal in both the "store says $5" and the "store has said
   * nothing" cases, and those need different treatment on any surface where the
   * store is the rail that will charge.
   *
   * @param {string} basePlanId e.g. "foundation-monthly"
   * @returns {string} the store-localized price, or "" if the cache has none
   */
  getCachedStorePrice(basePlanId) {
    return readCachedStorePrice(basePlanId);
  },

  /**
   * Get the store-localized price string for a base plan.
   * Falls back to USD price from pricingStore if not available.
   * @param {string} basePlanId
   * @returns {string} e.g. "$4.99" or "¥580"
   */
  getProductPrice(basePlanId) {
    if (!basePlanId || typeof basePlanId !== "string") return "";
    // Calls the module-private helper, not iapUtils.getCachedStorePrice: this
    // function's behavior must not become reassignable from outside, and it must
    // not depend on `this` (a caller that destructures it off iapUtils would
    // then throw instead of falling back).
    const cached = readCachedStorePrice(basePlanId);
    if (cached) return cached;
    // Fallback to the plan store USD price.
    const plan = pricingStore.getPlanByBasePlanId(basePlanId);
    if (plan && plan.id !== "free") {
      const isYearly = basePlanId.includes("yearly");
      return `$${isYearly ? plan.yearlyPrice : plan.monthlyPrice}`;
    }
    return "";
  },

  /**
   * Test seam: drive the module-private price cache from unit tests. Replaces
   * the whole cache, so passing {} clears it.
   *
   * The only production writer is the productUpdated callback registered inside
   * initialize(), which needs a live CdvPurchase store, so without this seam
   * every cache-HIT path is untestable and getCachedStorePrice would ship
   * unproven. Mirrors platformBilling._setIosStorefrontCountryForTest, and like
   * that one it ships in the production bundle — accepted for consistency with
   * the existing pattern rather than overlooked.
   *
   * Nothing outside tests may call it: a caller could otherwise make
   * getCachedStorePrice report a price the store never quoted, on surfaces whose
   * whole purpose is to show what the store will charge. That is an invariant
   * about our own source, not a security boundary (anything running in the
   * bundle can reach the module either way), and tests/unit/iapUtils.test.js
   * enforces it by scanning src/ for callers.
   *
   * Mutates the reactive object in place rather than reassigning it, so every
   * computed already tracking the cache keeps its dependency.
   */
  _setProductPricesForTest(entries) {
    Object.keys(productPrices).forEach((key) => delete productPrices[key]);
    Object.assign(productPrices, entries || {});
  },

  /**
   * Check if a product is currently owned (active subscription).
   * @param {string} productId
   * @returns {boolean}
   */
  isOwned(productId) {
    if (!store) return false;
    return store.owned(productId);
  },

  /**
   * Restore previous purchases. Useful after reinstall.
   */
  async restorePurchases() {
    if (!store) {
      console.error("IAP store not initialized");
      return false;
    }
    try {
      await store.restorePurchases();
      return true;
    } catch (error) {
      console.error("Restore purchases failed:", error);
      return false;
    }
  },

  /**
   * Check if the IAP store is ready.
   */
  isReady() {
    return initialized && store !== null;
  },
};
